/**
 * TanStack Query hooks for Lanista (free-weight) plans.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../client'
import type {
  LanistaPlanOut,
  LanistaPlanSummaryOut,
} from '../types'
import { OPTIONAL_QUERY_DEFAULTS, noneOn404 } from './shared'

/**
 * Fetch the currently active Lanista plan with exercises.
 * Returns null when no plan is active (the endpoint's 404); any other failure
 * is an error.
 * Query key: ['lanista', 'plan']
 */
export function useLanistaPlan() {
  return useQuery({
    queryKey: ['lanista', 'plan'],
    queryFn: () => noneOn404(apiFetch<LanistaPlanOut>('/api/lanista/plan')),
    ...OPTIONAL_QUERY_DEFAULTS,
  })
}

/**
 * List every synced Lanista plan (active + former) as lightweight summaries.
 * Query key: ['lanista', 'plans']
 */
export function useLanistaPlans() {
  return useQuery({
    queryKey: ['lanista', 'plans'],
    queryFn: () => apiFetch<LanistaPlanSummaryOut[]>('/api/lanista/plans'),
  })
}

/**
 * Activate or deactivate a Lanista (free-weight) plan — opt in/out of free
 * weights while keeping the plan and its history. Invalidates the plan list
 * and the active-plan query.
 */
export function useUpdateLanistaPlanStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'active' | 'inactive' }) =>
      apiFetch<LanistaPlanSummaryOut>(`/api/lanista/plans/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lanista', 'plans'] })
      queryClient.invalidateQueries({ queryKey: ['lanista', 'plan'] })
    },
  })
}
